import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import {
  CreditCard,
  DollarSign,
  HelpCircle,
  Sparkles,
  UserX,
} from 'lucide-react';

import { useEffect, useState } from 'react';

import { Label } from '~entities/dashboard/api/get-accounts-receivable-student-grouping';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

import { BalanceDueTab, BalanceDueRangeSelect } from './balance-due-tab';
import { CardExpiringTab } from './card-expiring-tab';
import { NoAttendanceTab } from './no-attendance-tab';
import { TrialsTab } from './trials-tab';

interface StudentRequiringAttentionSectionProps {
  sessionId: string;
}

export function StudentRequiringAttentionSection({
  sessionId,
}: StudentRequiringAttentionSectionProps) {
  const { hasSuperAdminAccess } = useCurrentUserRole();
  const tabsList = [
    ...(hasSuperAdminAccess
      ? [{ id: 'balance', label: 'Balance Due', icon: DollarSign }]
      : []),
    { id: 'no-attendance', label: 'No Attendance', icon: UserX },
    ...(hasSuperAdminAccess
      ? [{ id: 'card-expiring', label: 'Card Expiring', icon: CreditCard }]
      : []),
    { id: 'trial', label: 'Trial This Month', icon: Sparkles },
  ];
  const [activeTab, setActiveTab] = useState(tabsList[0]?.id ?? 'no-attendance');
  const [balanceRange, setBalanceRange] = useState<Label>(Label.Days0To30);

  useEffect(() => {
    if (!hasSuperAdminAccess) {
      return;
    }
    setActiveTab((current) =>
      current === 'no-attendance' ? 'balance' : current,
    );
  }, [hasSuperAdminAccess]);

  return (
    <Card className='w-full min-w-0 max-w-full overflow-hidden'>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
        <div className='flex min-w-0 items-center gap-2'>
          <CardTitle className='truncate text-lg font-semibold text-[#000814]'>
            Students Requiring Attention
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className='size-4' />
              </TooltipTrigger>
              <TooltipContent className='max-w-xs text-sm'>
                Attention highlights students who may need action — unpaid
                balances, low attendance, expiring cards, or missing policies.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>

      <CardContent className='min-w-0 max-w-full overflow-x-auto'>
        <div className='w-full min-w-0 max-w-full pb-4'>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className='w-full min-w-0 max-w-full bg-transparent'
          >
            <div className='mb-3 flex w-full min-w-0 max-w-full gap-2 md:hidden'>
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className='min-w-0 flex-1'>
                  <SelectValue placeholder='Select category' />
                </SelectTrigger>
                <SelectContent>
                  {tabsList.map((filter) => (
                    <SelectItem key={filter.id} value={filter.id}>
                      {filter.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasSuperAdminAccess && activeTab === 'balance' && (
                <BalanceDueRangeSelect
                  value={balanceRange}
                  onChange={setBalanceRange}
                  className='w-[7.25rem] shrink-0'
                />
              )}
            </div>
            <TabsList className='hidden h-auto w-full grid-cols-2 gap-2 bg-transparent md:grid lg:grid-cols-4'>
              {tabsList.map((filter) => {
                const Icon = filter.icon;
                return (
                  <TabsTrigger
                    key={filter.id}
                    value={filter.id}
                    className='flex items-center gap-1.5 rounded-lg border bg-transparent px-3 py-1.5 text-xs font-medium transition-all duration-100 hover:shadow-sm active:scale-[0.98]'
                  >
                    <Icon className='size-3.5' />
                    {filter.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {hasSuperAdminAccess && (
              <TabsContent value='balance'>
                {activeTab === 'balance' && (
                  <BalanceDueTab
                    sessionId={sessionId}
                    selectedRange={balanceRange}
                    onRangeChange={setBalanceRange}
                    hideRangeSelectOnMobile
                  />
                )}
              </TabsContent>
            )}
            <TabsContent value='no-attendance'>
              {activeTab === 'no-attendance' && (
                <NoAttendanceTab sessionId={sessionId} />
              )}
            </TabsContent>
            {hasSuperAdminAccess && (
              <TabsContent value='card-expiring'>
                {activeTab === 'card-expiring' && <CardExpiringTab />}
              </TabsContent>
            )}
            <TabsContent value='trial'>
              {activeTab === 'trial' && <TrialsTab />}
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
}
